import type { AppointmentStatus, AppointmentView, CheckInView, VisitServiceRequest, EventDetail, EventInput, EventOccurrenceView, EventSummary } from "@vaulted/shared";
import { EVENT_FONTS, HERO_PHOTO, STATUS_LABELS, formatCents, isOpenStatus, parseDollars, gfeOk, type EventAsset } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { errorMessage, get, post, put } from "../api";
import { AddressField } from "../components/AddressField";
import { GfeBadge } from "../components/Gfe";
import { ServicesPicker } from "../components/ServicesPicker";
import { FormsBadge, NEXT_ACTION, STATUS_TONE, time12 } from "../components/AppointmentDialog";
import { DateOfBirthField, Alert, Badge, Button, Checkbox, Dialog, Field, Loading, PageHeader, Panel, PanelHeader, SelectField, TabButtons, TextAreaField, cx } from "../components/ui";
import { useSession } from "../session";
import { useCatalog } from "./Catalog";
import { nowMinutesIn, todayIn, useCalendar } from "./Calendar";
import { displayName } from "./Clients";
import { BackLink, useBackTitle } from "../components/BackLink";

const dayLabel = (local: string) => new Date(`${local.slice(0, 10)}T12:00:00Z`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
const slugify = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);

/** Staff the event can use (from the calendar feed). */
function useStaff() {
  const day = todayIn();
  return useCalendar(day, day).data?.staff ?? [];
}

export function EventsPage() {
  const { can } = useSession();
  const nav = useNavigate();
  const list = useQuery({ queryKey: ["events"], queryFn: () => get<EventSummary[]>("/api/events") });
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const create = useMutation({ mutationFn: () => post<EventDetail>("/api/events", { name, slug: slugify(name) }), onSuccess: (e) => nav(`/events/${e.id}`) });
  const active = (list.data ?? []).filter((e) => e.status === "active");
  const archived = (list.data ?? []).filter((e) => e.status === "archived");

  return (
    <div className="flex max-w-4xl flex-col gap-4">
      <PageHeader
        title="Events"
        description="Each event has its own booking page, menu, prices, and dates with their own nurses and chairs."
        action={can("events.manage") && <Button variant="primary" onClick={() => setCreating(true)}>New event</Button>}
      />
      {list.isLoading ? (
        <Loading />
      ) : list.error ? (
        <Alert>{errorMessage(list.error)}</Alert>
      ) : !active.length ? (
        <p className="py-6 text-ink-soft">No events yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {active.map((e) => (
            <li key={e.id}>
              <Link to={`/events/${e.id}`} className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-[var(--radius-panel)] border border-line bg-white px-4 py-3 hover:border-brand">
                <span className="font-semibold">{e.name}</span>
                {e.host && <span className="text-sm text-ink-soft">{e.host}</span>}
                <span className="ml-auto text-sm text-muted">{e.nextDate ? `Next: ${dayLabel(e.nextDate)}, ${time12(e.nextDate)}` : "No upcoming dates"}</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {archived.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer font-semibold text-muted">Archived ({archived.length})</summary>
          <ul className="mt-2 flex flex-col gap-1">
            {archived.map((e) => (
              <li key={e.id}>
                <Link to={`/events/${e.id}`} className="underline">
                  {e.name}
                </Link>
              </li>
            ))}
          </ul>
        </details>
      )}
      {creating && (
        <Dialog
          title="New event"
          onClose={() => setCreating(false)}
          footer={
            <>
              <Button variant="quiet" onClick={() => setCreating(false)}>
                Cancel
              </Button>
              <Button variant="primary" busy={create.isPending} disabled={slugify(name).length < 3} onClick={() => create.mutate()}>
                Create
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-3">
            <Field label="Name" autoFocus value={name} onChange={(e) => setName(e.target.value)} hint={name ? `Link: /${slugify(name)}` : "For example, Noun Hotel"} />
            {create.error && <Alert>{errorMessage(create.error)}</Alert>}
          </div>
        </Dialog>
      )}
    </div>
  );
}

export function EventPage() {
  const { id = "" } = useParams();
  const { can } = useSession();
  const qc = useQueryClient();
  const ev = useQuery({ queryKey: ["event", id], queryFn: () => get<EventDetail>(`/api/events/${id}`) });
  const [tab, setTab] = useState<"dates" | "details" | "menu">("dates");
  const setEvent = (e: EventDetail) => {
    qc.setQueryData(["event", id], e);
    void qc.invalidateQueries({ queryKey: ["events"] });
    void qc.invalidateQueries({ queryKey: ["calendar"] });
  };
  const status = useMutation({ mutationFn: (s: "active" | "archived") => post<EventDetail>(`/api/events/${id}/status`, { status: s }), onSuccess: setEvent });
  useBackTitle(ev.data?.name);

  if (ev.isLoading) return <Loading />;
  if (ev.error || !ev.data) return <Alert>{errorMessage(ev.error)}</Alert>;
  const e = ev.data;
  const manage = can("events.manage");

  return (
    <div className="flex max-w-4xl flex-col gap-4">
      <BackLink to="/events" label="Events" />
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="page-title">{e.name}</h1>
          <p className="mt-1 text-sm text-ink-soft">
            Booking link:{" "}
            <a href={e.link} target="_blank" rel="noreferrer" className="font-semibold text-brand underline">
              {e.link}
            </a>
            <button className="ml-2 text-muted underline" onClick={() => void navigator.clipboard?.writeText(e.link)}>
              Copy
            </button>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {e.status === "archived" && <Badge tone="off">Archived</Badge>}
          {manage && (
            <Button variant="quiet" busy={status.isPending} onClick={() => status.mutate(e.status === "active" ? "archived" : "active")}>
              {e.status === "active" ? "Archive" : "Restore"}
            </Button>
          )}
        </div>
      </header>
      {e.status === "active" && e.missing.length > 0 && (
        <Alert tone="info">The booking page isn't taking bookings yet. It still needs {e.missing.join(", ").replace(/, ([^,]*)$/, " and $1")}.</Alert>
      )}
      <TabButtons
        label="Event sections"
        value={tab}
        onChange={setTab}
        items={[
          ["dates", `Dates (${e.occurrences.filter((o) => !o.cancelled).length})`],
          ["menu", `Menu (${e.menu.length})`],
          ["details", "Details and page"],
        ]}
      />
      {tab === "dates" && <DatesTab e={e} manage={manage} onSaved={setEvent} />}
      {tab === "menu" && <MenuTab e={e} manage={manage} onSaved={setEvent} />}
      {tab === "details" && <DetailsTab e={e} manage={manage} onSaved={setEvent} />}
    </div>
  );
}

function DatesTab({ e, manage, onSaved }: { e: EventDetail; manage: boolean; onSaved: (e: EventDetail) => void }) {
  const [editing, setEditing] = useState<EventOccurrenceView | "new" | null>(null);
  const cancel = useMutation({ mutationFn: (o: EventOccurrenceView) => post<EventDetail>(`/api/events/${e.id}/occurrences/${o.id}/cancel`, { cancelled: !o.cancelled }), onSuccess: onSaved });
  // Dates are in the clinic's time: compare with its clock, not UTC (5 or 6 hours ahead).
  const tz = useSession().me?.clinic.timezone;
  const mins = nowMinutesIn(tz);
  const now = `${todayIn(tz)}T${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
  const upcoming = e.occurrences.filter((o) => o.end >= now);
  const past = e.occurrences.filter((o) => o.end < now);
  const row = (o: EventOccurrenceView) => (
    <li key={o.id} className={cx("flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-mist px-5 py-3 last:border-0", o.cancelled && "opacity-60")}>
      <span className="w-56 font-semibold">
        {dayLabel(o.start)}
        {o.title && <span className="block text-sm">{o.title}</span>}
        <span className="block text-sm font-normal text-ink-soft">
          {time12(o.start)}–{time12(o.end)}
        </span>
      </span>
      <span className="min-w-0 flex-1 text-sm">
        {o.chairCount} chair{o.chairCount === 1 ? "" : "s"} · {o.staff.length ? o.staff.map((s) => s.name).join(", ") : <span className="text-wait">No nurses yet</span>}
        <span className="block text-muted">{o.booked} booked{o.notes && ` · ${o.notes}`}</span>
      </span>
      {o.cancelled ? (
        <Badge tone="off">Cancelled</Badge>
      ) : (
        <Link to={`/events/check-in/${o.id}`} className="text-sm font-semibold text-brand">
          Check-in
        </Link>
      )}
      {manage && (
        <span className="flex gap-1">
          <Button variant="quiet" onClick={() => setEditing(o)}>
            Edit
          </Button>
          <Button variant="quiet" busy={cancel.isPending && cancel.variables?.id === o.id} onClick={() => cancel.mutate(o)}>
            {o.cancelled ? "Restore" : "Cancel"}
          </Button>
        </span>
      )}
    </li>
  );
  return (
    <div className="flex flex-col gap-4">
      {cancel.error && <Alert>{errorMessage(cancel.error)}</Alert>}
      <Panel>
        <PanelHeader
          title="Upcoming dates"
          description="Each date has its own nurses and chairs. Assigned nurses are working the event, whatever their weekly hours."
          action={manage && <Button onClick={() => setEditing("new")}>Add dates</Button>}
        />
        {upcoming.length ? <ul>{upcoming.map(row)}</ul> : <p className="px-5 py-4 text-sm text-muted">No upcoming dates.</p>}
      </Panel>
      {past.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer font-semibold text-muted">Past dates ({past.length})</summary>
          <Panel className="mt-2">
            <ul>{past.map(row)}</ul>
          </Panel>
        </details>
      )}
      {editing && <OccurrenceDialog e={e} o={editing === "new" ? null : editing} onClose={() => setEditing(null)} onSaved={(x) => (onSaved(x), setEditing(null))} />}
    </div>
  );
}

function OccurrenceDialog({ e, o, onClose, onSaved }: { e: EventDetail; o: EventOccurrenceView | null; onClose: () => void; onSaved: (e: EventDetail) => void }) {
  const staff = useStaff();
  const last = e.occurrences.at(-1);
  const [date, setDate] = useState(o?.start.slice(0, 10) ?? todayIn());
  const [start, setStart] = useState(o?.start.slice(11, 16) ?? last?.start.slice(11, 16) ?? "09:00");
  const [end, setEnd] = useState(o?.end.slice(11, 16) ?? last?.end.slice(11, 16) ?? "21:00");
  const [chairs, setChairs] = useState(String(o?.chairCount ?? last?.chairCount ?? 2));
  const [staffIds, setStaffIds] = useState<string[]>(o?.staff.map((s) => s.id) ?? []);
  const [notes, setNotes] = useState(o?.notes ?? "");
  const [title, setTitle] = useState(o?.title ?? "");
  const [subtitle, setSubtitle] = useState(o?.subtitle ?? "");
  const [repeat, setRepeat] = useState("");
  const endDate = end <= start ? new Date(Date.parse(`${date}T12:00:00Z`) + 86_400_000).toISOString().slice(0, 10) : date;
  const save = useMutation({
    mutationFn: () => {
      const body = { start: `${date}T${start}`, end: `${endDate}T${end}`, chairCount: Number(chairs) || 0, title, subtitle, staffIds, notes, repeatWeeklyUntil: repeat || null };
      return o ? put<EventDetail>(`/api/events/${e.id}/occurrences/${o.id}`, body) : post<EventDetail>(`/api/events/${e.id}/occurrences`, body);
    },
    onSuccess: onSaved,
  });
  return (
    <Dialog
      title={o ? "Edit date" : "Add dates"}
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-3 gap-3">
          <Field label="Date" type="date" value={date} onChange={(ev) => setDate(ev.target.value)} />
          <Field label="Starts" type="time" step={900} value={start} onChange={(ev) => setStart(ev.target.value)} />
          <Field label="Ends" type="time" step={900} value={end} onChange={(ev) => setEnd(ev.target.value)} hint={endDate !== date ? "Next day" : undefined} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Title on the event page (optional)" placeholder="OU vs South Carolina" maxLength={80} value={title} onChange={(ev) => setTitle(ev.target.value)} />
          <Field label="Under the title (optional)" placeholder="The weekday" maxLength={80} value={subtitle} onChange={(ev) => setSubtitle(ev.target.value)} />
        </div>
        <Field label="Chairs on site" type="number" min={0} max={100} value={chairs} onChange={(ev) => setChairs(ev.target.value)} hint="Event chairs, separate from the clinic's: at most this many people at the same time." />
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-1 text-sm font-semibold text-ink-soft">Nurses working it</legend>
          {staff.map((s) => (
            <Checkbox key={s.id} label={s.name} checked={staffIds.includes(s.id)} onChange={(on) => setStaffIds(on ? [...staffIds, s.id] : staffIds.filter((x) => x !== s.id))} />
          ))}
        </fieldset>
        <Field label="Notes for staff (optional)" value={notes} onChange={(ev) => setNotes(ev.target.value)} />
        {!o && <Field label="Repeat every week until (optional)" type="date" min={date} value={repeat} onChange={(ev) => setRepeat(ev.target.value)} />}
        {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      </div>
    </Dialog>
  );
}

function MenuTab({ e, manage, onSaved }: { e: EventDetail; manage: boolean; onSaved: (e: EventDetail) => void }) {
  const catalog = useCatalog();
  type Row = { on: boolean; price: string; name: string; description: string; groupLabel: string; role: "drip" | "extra" | "addon" };
  const items = useMemo(() => (catalog.data?.items ?? []).filter((i) => (i.type === "service" || i.type === "add_on") && i.status === "active"), [catalog.data]);
  const [rows, setRows] = useState<Record<string, Row>>({});
  const [percentOff, setPercentOff] = useState("");
  useEffect(() => {
    const next: Record<string, Row> = {};
    for (const i of items) {
      const m = e.menu.find((x) => x.catalogItemId === i.id);
      next[i.id] = { on: !!m, price: ((m?.priceCents ?? i.eventPriceCents ?? i.priceCents) / 100).toFixed(2), name: m?.name ?? "", description: m?.description ?? "", groupLabel: m?.groupLabel ?? "", role: m?.role ?? "drip" };
    }
    setRows(next);
  }, [items, e.menu]);
  const save = useMutation({
    mutationFn: () =>
      put<EventDetail>(`/api/events/${e.id}/menu`, {
        items: items
          .filter((i) => rows[i.id]?.on)
          .map((i) => ({ catalogItemId: i.id, priceCents: parseDollars(rows[i.id]!.price) ?? i.priceCents, name: rows[i.id]!.name.trim() || null, description: rows[i.id]!.description.trim() || null, groupLabel: rows[i.id]!.groupLabel.trim() || null, role: i.type === "service" ? rows[i.id]!.role : "drip" })),
      }),
    onSuccess: onSaved,
  });
  if (catalog.isLoading) return <Loading />;
  const set = (id: string, patch: Partial<Row>) => setRows({ ...rows, [id]: { ...rows[id]!, ...patch } });
  const categoryName = (id: string | null) => catalog.data?.categories.find((c) => c.id === id)?.name ?? "";
  const section = (type: "service" | "add_on", title: string) => (
    <Panel>
      <PanelHeader title={title} />
      <ul>
        {items
          .filter((i) => i.type === type)
          .map((i) => {
            const r = rows[i.id];
            if (!r) return null;
            return (
              <li key={i.id} className="flex flex-col gap-2 border-b border-mist px-5 py-3 last:border-0">
                <div className="flex flex-wrap items-center gap-3">
                  <Checkbox label={i.name} checked={r.on} disabled={!manage} onChange={(on) => set(i.id, { on })} />
                  <span className="text-xs text-muted">
                    Clinic {formatCents(i.priceCents)}
                    {!i.availableClinic && !i.availableMobile && " · events only"}
                  </span>
                  {r.on && (
                    <span className="ml-auto flex items-center gap-1 text-sm">
                      $<input aria-label={`${i.name} event price`} disabled={!manage} className="w-24 rounded-[var(--radius-control)] border border-line px-2 py-1 text-right tabular-nums" inputMode="decimal" value={r.price} onChange={(ev) => set(i.id, { price: ev.target.value })} />
                    </span>
                  )}
                </div>
                {r.on && type === "service" && (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <SelectField label="On the page" disabled={!manage} value={r.role} onChange={(ev) => set(i.id, { role: ev.target.value as Row["role"] })}>
                      <option value="drip">A drip (pick one; add-ons go with it)</option>
                      <option value="extra">Recovery or extra (pick one, with or without a drip)</option>
                      <option value="addon">Add-on with a drip (check boxes, e.g. a shot)</option>
                    </SelectField>
                    {r.role !== "drip" ? (
                      <Field label="Section heading" disabled={!manage} placeholder={r.role === "extra" ? "Recovery" : "Shots"} maxLength={40} value={r.groupLabel} onChange={(ev) => set(i.id, { groupLabel: ev.target.value })} />
                    ) : (
                      <span />
                    )}
                    <Field label="Name on the event page (optional)" disabled={!manage} placeholder={i.name} value={r.name} onChange={(ev) => set(i.id, { name: ev.target.value })} />
                    <Field label="Description (optional)" disabled={!manage} placeholder={i.description.slice(0, 60)} value={r.description} onChange={(ev) => set(i.id, { description: ev.target.value })} />
                  </div>
                )}
                {r.on && type === "add_on" && (
                  <div className="grid gap-2 sm:grid-cols-3">
                    <Field label="Name on the event page (optional)" disabled={!manage} placeholder={i.name} value={r.name} onChange={(ev) => set(i.id, { name: ev.target.value })} />
                    <Field label="Short note (optional)" disabled={!manage} placeholder="nausea" maxLength={40} value={r.description} onChange={(ev) => set(i.id, { description: ev.target.value })} />
                    <Field label="Listed under" disabled={!manage} placeholder={categoryName(i.categoryId)} maxLength={40} value={r.groupLabel} onChange={(ev) => set(i.id, { groupLabel: ev.target.value })} />
                  </div>
                )}
              </li>
            );
          })}
      </ul>
    </Panel>
  );
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-ink-soft">Pick what this event offers and its prices. Add-ons (shots too) can only be booked with a drip; a recovery option can be booked with or without one, and shares the guest's chair. A price below the usual one shows the usual one struck through on the page. Items stay linked to the catalog, so recipes, build sheets, and charting work as usual. A service offered neither in the clinic nor mobile is "events only".</p>
      {section("service", "Services")}
      {manage && (
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Take this % off every add-on on the menu" inputMode="numeric" className="w-64" value={percentOff} onChange={(ev) => setPercentOff(ev.target.value.replace(/[^0-9]/g, "").slice(0, 2))} />
          <Button
            disabled={!percentOff}
            onClick={() => {
              const pct = Number(percentOff);
              const next = { ...rows };
              // From the usual price (the item's event price, else its clinic price); each can still be changed after.
              for (const i of items) if (i.type === "add_on" && next[i.id]?.on) next[i.id] = { ...next[i.id]!, price: ((Math.round(((i.eventPriceCents ?? i.priceCents) * (100 - pct)) / 100) / 100)).toFixed(2) };
              setRows(next);
            }}
          >
            Apply to add-on prices
          </Button>
          <span className="pb-2 text-xs text-muted">Then change any special (a $10 B-12) and save.</span>
        </div>
      )}
      {section("add_on", "Add-ons")}
      {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      {manage && (
        <div>
          <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
            Save menu
          </Button>
        </div>
      )}
    </div>
  );
}

function DetailsTab({ e, manage, onSaved }: { e: EventDetail; manage: boolean; onSaved: (e: EventDetail) => void }) {
  const [v, setV] = useState<EventInput>(() => ({
    name: e.name,
    slug: e.slug,
    host: e.host,
    venueName: e.venueName,
    venueAddress: e.venueAddress,
    intro: e.intro,
    staffNotes: e.staffNotes,
    logoKey: e.logoKey,
    primaryColor: e.primaryColor,
    accentColor: e.accentColor,
    heroImageKey: e.heroImageKey,
    heroFocus: e.heroFocus,
    bandImageKey: e.bandImageKey,
    heroEdge: e.heroEdge,
    edgeColor: e.edgeColor,
    heroTop: e.heroTop,
    cityLine: e.cityLine,
    headline: e.headline,
    addOnsNote: e.addOnsNote,
    headingFont: e.headingFont,
    bodyFont: e.bodyFont,
    backgroundColor: e.backgroundColor,
    cardColor: e.cardColor,
    textColor: e.textColor,
    unlisted: e.unlisted,
    bookingMode: e.bookingMode,
    slotMinutes: e.slotMinutes,
    maxGuests: e.maxGuests,
    depositPercent: e.depositPercent,
    questions: e.questions,
  }));
  const set = <K extends keyof EventInput>(k: K, value: EventInput[K]) => setV({ ...v, [k]: value });
  const addr = v.venueAddress ?? { line1: "", line2: "", city: "", state: "OK", postalCode: "" };
  const save = useMutation({ mutationFn: () => put<EventDetail>(`/api/events/${e.id}`, { ...v, venueAddress: addr.line1 ? addr : null }), onSuccess: onSaved });

  return (
    <form className="flex flex-col gap-4" onSubmit={(ev) => (ev.preventDefault(), save.mutate())}>
      <fieldset disabled={!manage} className="flex flex-col gap-4">
        <Panel>
          <PanelHeader title="Event" />
          <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
            <Field label="Name" required value={v.name} onChange={(ev) => set("name", ev.target.value)} />
            <Field label="Link name" required value={v.slug} onChange={(ev) => set("slug", slugify(ev.target.value))} hint={`/${v.slug}`} />
            <Field label="Host" value={v.host} onChange={(ev) => set("host", ev.target.value)} hint="Shown at the top of the page, e.g. The Noun Hotel" />
            <Field label="Venue" value={v.venueName} onChange={(ev) => set("venueName", ev.target.value)} />
            <AddressField
              label="Street address"
              placeholder=""
              value={addr.line1}
              onChange={(line1) => set("venueAddress", { ...addr, line1 })}
              onResolved={(r) => set("venueAddress", { ...addr, line1: r.line1, city: r.city, state: r.state, postalCode: r.postalCode, ...(r.line2 ? { line2: r.line2 } : {}) })}
            />
            <div className="grid grid-cols-3 gap-2">
              <Field label="City" value={addr.city} onChange={(ev) => set("venueAddress", { ...addr, city: ev.target.value })} />
              <Field label="State" value={addr.state} onChange={(ev) => set("venueAddress", { ...addr, state: ev.target.value })} />
              <Field label="ZIP" value={addr.postalCode} onChange={(ev) => set("venueAddress", { ...addr, postalCode: ev.target.value })} />
            </div>
            <TextAreaField className="sm:col-span-2" label="Notes for staff" rows={2} value={v.staffNotes} onChange={(ev) => set("staffNotes", ev.target.value)} />
          </div>
        </Panel>
        <Panel>
          <PanelHeader title="Booking" />
          <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
            <SelectField label="How guests come" value={v.bookingMode} onChange={(ev) => set("bookingMode", ev.target.value as EventInput["bookingMode"])}>
              <option value="both">Book a time, or walk up</option>
              <option value="slots">Book a time only</option>
              <option value="walk_ups">Walk-ups only (no online booking)</option>
            </SelectField>
            <Field label="Time slots every (minutes)" type="number" min={5} max={240} step={5} value={v.slotMinutes} onChange={(ev) => set("slotMinutes", Number(ev.target.value))} />
            <Field
              label="Deposit (% of total)"
              type="number"
              min={0}
              max={100}
              value={v.depositPercent ?? ""}
              onChange={(ev) => set("depositPercent", ev.target.value === "" ? null : Number(ev.target.value))}
              hint="Collected once card payments are set up."
            />
            <div className="sm:col-span-2">
              <p className="mb-1 text-sm font-semibold text-ink-soft">Extra questions (optional)</p>
              {v.questions.map((q, i) => (
                <div key={i} className="mb-2 flex items-center gap-3">
                  <input className="flex-1 rounded-[var(--radius-control)] border border-line px-3 py-2" aria-label="Question" value={q.label} onChange={(ev) => set("questions", v.questions.map((x, j) => (j === i ? { ...x, label: ev.target.value } : x)))} />
                  <Checkbox label="Required" checked={q.required} onChange={(on) => set("questions", v.questions.map((x, j) => (j === i ? { ...x, required: on } : x)))} />
                  <button type="button" className="text-sm text-muted hover:text-danger" onClick={() => set("questions", v.questions.filter((_, j) => j !== i))}>
                    Remove
                  </button>
                </div>
              ))}
              {v.questions.length < 5 && (
                <button type="button" className="text-sm font-semibold text-brand" onClick={() => set("questions", [...v.questions, { label: "", required: false }])}>
                  + Add a question
                </button>
              )}
            </div>
          </div>
        </Panel>
        <Panel>
          <PanelHeader title="Event page" description="The big photo, fonts, and colors. Colors left on the clinic's use the clinic's." />
          <div className="grid gap-5 px-5 py-5 sm:grid-cols-2">
            <HeroField className="sm:col-span-2" url={e.heroUrl} imageKey={v.heroImageKey} focus={v.heroFocus} onChange={(key, focus) => setV({ ...v, heroImageKey: key, heroFocus: focus })} />
            <p className="text-sm text-ink-soft sm:col-span-2">Text on the photo, top to bottom. Every line is optional; blank ones don't show.</p>
            <Field label="Hero top" placeholder="NOUN" maxLength={80} value={v.heroTop} onChange={(ev) => set("heroTop", ev.target.value)} hint="A logo, if uploaded, shows here instead." />
            <Field label="Small line under hero top" placeholder="NORMAN, OK" maxLength={80} value={v.cityLine} onChange={(ev) => set("cityLine", ev.target.value)} />
            <Field label="Hero bottom" placeholder="Book your game day IV" maxLength={120} value={v.headline} onChange={(ev) => set("headline", ev.target.value)} />
            <TextAreaField label="Hero bottom sub-headline" rows={3} value={v.intro} onChange={(ev) => set("intro", ev.target.value)} />
            <AssetField kind="logo" label="Logo or wordmark (optional)" hint="PNG or SVG with a clear background; replaces the hero top text." accept="image/png,image/jpeg,image/svg+xml,image/webp" url={e.logoUrl} imageKey={v.logoKey} onChange={(key) => set("logoKey", key)} />
            <div className="flex flex-col gap-3">
              <SelectField label="Hero bottom edge" value={v.heroEdge} onChange={(ev) => set("heroEdge", ev.target.value as EventInput["heroEdge"])}>
                <option value="line">A line in a color</option>
                <option value="pattern">A pattern strip</option>
                <option value="fade">The photo fades into the page</option>
              </SelectField>
              {v.heroEdge === "line" && <ColorField label="Line color" unset="Same as headings" value={v.edgeColor} onChange={(c) => set("edgeColor", c)} />}
              {v.heroEdge === "pattern" && (
                <AssetField kind="band" label="Pattern strip" hint="Repeats sideways under the photo and above the footer. SVG works best (sharp at any size); or PNG/JPG about 2000 × 60 px. Until one's uploaded, it's a line." accept="image/svg+xml,image/png,image/jpeg,image/webp" url={e.bandUrl} imageKey={v.bandImageKey} onChange={(key) => set("bandImageKey", key)} />
              )}
            </div>
            <Field className="sm:col-span-2" label="Note above the add-ons" placeholder="Optional. Each add-on is $49." maxLength={200} value={v.addOnsNote} onChange={(ev) => set("addOnsNote", ev.target.value)} />
            <FontField label="Heading font" value={v.headingFont} onChange={(f) => set("headingFont", f)} />
            <FontField label="Body font" value={v.bodyFont} onChange={(f) => set("bodyFont", f)} />
            <div className="flex flex-wrap gap-4 sm:col-span-2">
              <ColorField label="Background" value={v.backgroundColor} onChange={(c) => set("backgroundColor", c)} />
              <ColorField label="Cards and fields" value={v.cardColor} onChange={(c) => set("cardColor", c)} />
              <ColorField label="Text" value={v.textColor} onChange={(c) => set("textColor", c)} />
              <ColorField label="Headings" value={v.primaryColor} onChange={(c) => set("primaryColor", c)} />
              <ColorField label="Button" value={v.accentColor} onChange={(c) => set("accentColor", c)} />
            </div>
            <Checkbox label="Private: only people with the link can find it" checked={v.unlisted} onChange={(on) => set("unlisted", on)} />
          </div>
        </Panel>
      </fieldset>
      {save.error && <Alert>{errorMessage(save.error)}</Alert>}
      {save.isSuccess && !save.isPending && <Alert tone="ok">Saved.</Alert>}
      {manage && (
        <div>
          <Button variant="primary" type="submit" busy={save.isPending}>
            Save
          </Button>
        </div>
      )}
    </form>
  );
}

/** Upload a file for the event page; returns its storage key. */
function useAssetUpload(kind: EventAsset) {
  return useMutation({
    mutationFn: async (file: File) => {
      const type = file.type || (kind === "font" ? `font/${file.name.split(".").pop()?.toLowerCase()}` : "");
      const up = await post<{ key: string; url: string; headers: Record<string, string> }>("/api/events/asset-upload", { kind, contentType: type });
      const res = await fetch(up.url, { method: "PUT", headers: up.headers, body: file });
      if (!res.ok) throw new Error("The file couldn't be uploaded. Try again.");
      return { key: up.key, preview: URL.createObjectURL(file) };
    },
  });
}

function AssetField({ kind, label, hint, accept, url, imageKey, onChange }: { kind: EventAsset; label: string; hint: string; accept: string; url: string | null; imageKey: string | null; onChange: (key: string | null) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState(url);
  const upload = useAssetUpload(kind);
  return (
    <div>
      <p className="mb-1 text-sm font-semibold text-ink-soft">{label}</p>
      <div className="flex items-center gap-3">
        {imageKey && preview && <img src={preview} alt="" className="h-10 max-w-48 rounded bg-ink object-contain p-1" />}
        <Button type="button" busy={upload.isPending} onClick={() => fileRef.current?.click()}>
          {imageKey ? "Change" : "Upload"}
        </Button>
        {imageKey && (
          <button type="button" className="text-sm text-muted underline" onClick={() => onChange(null)}>
            Remove
          </button>
        )}
      </div>
      <p className="mt-1 text-xs text-muted">{hint}</p>
      <input ref={fileRef} type="file" accept={accept} hidden onChange={(ev) => ev.target.files?.[0] && upload.mutate(ev.target.files[0], { onSuccess: (r) => (setPreview(r.preview), onChange(r.key)) })} />
      {upload.error && <p className="mt-1 text-sm text-danger">{errorMessage(upload.error)}</p>}
    </div>
  );
}

/** How a hero photo of this width will look. */
const heroQuality = (width: number) =>
  width < HERO_PHOTO.minWidth
    ? { ok: false, note: `Too small to look sharp (${width} px wide; at least ${HERO_PHOTO.minWidth} needed). Zoom out, or use a bigger photo.` }
    : width < HERO_PHOTO.goodWidth
      ? { ok: true, note: `${width} px wide: may look a little soft on large screens. ${HERO_PHOTO.goodWidth}+ looks best.` }
      : { ok: true, note: null };

/** The hero photo: required, cropped to 2:1, at least HERO_PHOTO.minWidth wide; click it to pick the spot kept in view on phones. */
function HeroField({ className, url, imageKey, focus, onChange }: { className?: string; url: string | null; imageKey: string | null; focus: EventInput["heroFocus"]; onChange: (key: string | null, focus: EventInput["heroFocus"]) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState(url);
  const [problem, setProblem] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [cropping, setCropping] = useState<{ src: string; w: number; h: number } | null>(null);
  const upload = useAssetUpload("hero");
  const send = (file: File, width: number) => {
    setNote(heroQuality(width).note);
    upload.mutate(file, { onSuccess: (r) => (setPreview(r.preview), onChange(r.key, { x: 50, y: 50 })) });
  };
  const pick = (file: File) => {
    setProblem(null);
    setNote(null);
    const src = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      // Cropping can't add pixels: too narrow already is a hard stop.
      if (w < HERO_PHOTO.minWidth) {
        URL.revokeObjectURL(src);
        setProblem(`That photo is ${w} × ${h}. It needs to be at least ${HERO_PHOTO.minWidth} px wide to look sharp on a computer.`);
        return;
      }
      // Already wide (within 3% of 2:1) and not oversized: as it is. Anything else gets the crop step.
      if (Math.abs(w / h - HERO_PHOTO.ratio) / HERO_PHOTO.ratio <= 0.03 && w <= HERO_PHOTO.maxWidth) {
        URL.revokeObjectURL(src);
        send(file, w);
        return;
      }
      setCropping({ src, w, h });
    };
    img.onerror = () => setProblem("That file couldn't be read as a photo.");
    img.src = src;
  };
  const f = focus ?? { x: 50, y: 50 };
  return (
    <div className={className}>
      <p className="mb-1 text-sm font-semibold text-ink-soft">
        Hero photo <span className="text-danger">*</span>
      </p>
      <p className="mb-2 text-xs text-muted">
        JPG, WebP, or PNG, at least {HERO_PHOTO.minWidth} px wide ({HERO_PHOTO.goodWidth}+ looks best). Photos that aren't already wide are cropped to 2:1 first. Then click the photo to mark what to keep in view on phones.
      </p>
      {imageKey && preview && (
        <button
          type="button"
          aria-label="Pick the spot to keep in view"
          className="relative mb-2 block aspect-[2/1] w-full max-w-xl overflow-hidden rounded-[var(--radius-control)] bg-ink"
          onClick={(ev) => {
            const r = ev.currentTarget.getBoundingClientRect();
            onChange(imageKey, { x: Math.round(((ev.clientX - r.left) / r.width) * 100), y: Math.round(((ev.clientY - r.top) / r.height) * 100) });
          }}
        >
          <img src={preview} alt="" className="h-full w-full object-cover" />
          <span className="absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow" style={{ left: `${f.x}%`, top: `${f.y}%` }} />
        </button>
      )}
      <div className="flex items-center gap-3">
        <Button type="button" busy={upload.isPending} onClick={() => fileRef.current?.click()}>
          {imageKey ? "Change photo" : "Upload photo"}
        </Button>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/webp,image/png"
        hidden
        onChange={(ev) => {
          const file = ev.target.files?.[0];
          ev.target.value = "";
          if (file) pick(file);
        }}
      />
      {problem && <p className="mt-1 text-sm text-danger">{problem}</p>}
      {note && !problem && <p className="mt-1 text-sm text-wait">{note}</p>}
      {upload.error && <p className="mt-1 text-sm text-danger">{errorMessage(upload.error)}</p>}
      {cropping && (
        <CropDialog
          image={cropping}
          onClose={() => (URL.revokeObjectURL(cropping.src), setCropping(null))}
          onCropped={(file, width) => {
            URL.revokeObjectURL(cropping.src);
            setCropping(null);
            send(file, width);
          }}
        />
      )}
    </div>
  );
}

/**
 * Crop to 2:1: drag the photo inside a fixed wide frame and zoom with the slider. The result keeps the
 * photo's own pixels (never enlarged); Save is off while the crop would be too small to look sharp.
 */
function CropDialog({ image, onClose, onCropped }: { image: { src: string; w: number; h: number }; onClose: () => void; onCropped: (file: File, width: number) => void }) {
  const frameW = 560;
  const frameH = frameW / HERO_PHOTO.ratio;
  // Smallest scale (screen px per photo px) that still covers the frame.
  const cover = Math.max(frameW / image.w, frameH / image.h);
  const [zoom, setZoom] = useState(1);
  const scale = cover * zoom;
  const [pos, setPos] = useState({ x: (frameW - image.w * cover) / 2, y: (frameH - image.h * cover) / 2 });
  const drag = useRef<{ x: number; y: number; px: number; py: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const clamp = (p: { x: number; y: number }, s = scale) => ({ x: Math.min(0, Math.max(frameW - image.w * s, p.x)), y: Math.min(0, Math.max(frameH - image.h * s, p.y)) });
  // The crop in photo pixels.
  const crop = { x: -pos.x / scale, y: -pos.y / scale, w: frameW / scale, h: frameH / scale };
  const outW = Math.round(Math.min(crop.w, HERO_PHOTO.maxWidth));
  const q = heroQuality(outW);
  const setZoomKeepCenter = (z: number) => {
    const s = cover * z;
    const cx = (frameW / 2 - pos.x) / scale;
    const cy = (frameH / 2 - pos.y) / scale;
    setZoom(z);
    setPos(clamp({ x: frameW / 2 - cx * s, y: frameH / 2 - cy * s }, s));
  };
  const save = () => {
    setBusy(true);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = Math.round(outW / HERO_PHOTO.ratio);
      canvas.getContext("2d")!.drawImage(img, crop.x, crop.y, crop.w, crop.h, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        setBusy(false);
        if (blob) onCropped(new File([blob], "hero.jpg", { type: "image/jpeg" }), outW);
      }, "image/jpeg", 0.9);
    };
    img.src = image.src;
  };
  return (
    <Dialog
      title="Crop the hero photo"
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={busy} disabled={!q.ok} onClick={save}>
            Use this crop
          </Button>
        </>
      }
    >
      <p className="mb-3 text-sm text-ink-soft">Drag to move the photo; zoom with the slider. The frame is the wide shape the page uses on computers; phones show a narrower part of it.</p>
      <div
        className="relative mx-auto cursor-grab touch-none overflow-hidden rounded-[var(--radius-control)] bg-ink select-none active:cursor-grabbing"
        style={{ width: frameW, height: frameH, maxWidth: "100%" }}
        onPointerDown={(ev) => {
          ev.currentTarget.setPointerCapture(ev.pointerId);
          drag.current = { x: ev.clientX, y: ev.clientY, px: pos.x, py: pos.y };
        }}
        onPointerMove={(ev) => {
          const d = drag.current;
          if (d) setPos(clamp({ x: d.px + ev.clientX - d.x, y: d.py + ev.clientY - d.y }));
        }}
        onPointerUp={() => (drag.current = null)}
      >
        <img src={image.src} alt="" draggable={false} className="absolute max-w-none origin-top-left" style={{ width: image.w * scale, height: image.h * scale, left: pos.x, top: pos.y }} />
      </div>
      <label className="mx-auto mt-3 flex max-w-[560px] items-center gap-3 text-sm">
        Zoom
        <input type="range" min={1} max={4} step={0.01} value={zoom} onChange={(ev) => setZoomKeepCenter(Number(ev.target.value))} className="flex-1" />
      </label>
      <p className={cx("mx-auto mt-2 max-w-[560px] text-sm", !q.ok ? "text-danger" : q.note ? "text-wait" : "text-ok")}>
        Crop: {outW} × {Math.round(outW / HERO_PHOTO.ratio)} px. {q.note ?? "Sharp on every screen."}
      </p>
    </Dialog>
  );
}

/** A built-in font, or an uploaded one (WOFF2 best). */
function FontField({ label, value, onChange }: { label: string; value: EventInput["headingFont"]; onChange: (f: EventInput["headingFont"]) => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useAssetUpload("font");
  const builtIn = (EVENT_FONTS as readonly string[]).includes(value?.family ?? "");
  return (
    <div>
      <SelectField
        label={label}
        value={!value ? "" : value.key ? "upload" : value.family}
        onChange={(ev) => (ev.target.value === "upload" ? fileRef.current?.click() : onChange(ev.target.value ? { family: ev.target.value, key: null } : null))}
      >
        <option value="">The clinic's</option>
        {EVENT_FONTS.map((f) => (
          <option key={f} value={f}>
            {f}
          </option>
        ))}
        <option value="upload">{value?.key ? `Uploaded: ${value.family}` : "Upload a font…"}</option>
      </SelectField>
      {!builtIn && value?.key && (
        <button type="button" className="mt-1 text-sm text-muted underline" onClick={() => fileRef.current?.click()}>
          Upload a different file
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept=".woff2,.woff,.ttf,.otf"
        hidden
        onChange={(ev) => {
          const file = ev.target.files?.[0];
          if (file) upload.mutate(file, { onSuccess: (r) => onChange({ family: file.name.replace(/\.[a-z0-9]+$/i, "").slice(0, 60), key: r.key }) });
        }}
      />
      {upload.isPending && <p className="mt-1 text-sm text-muted">Uploading…</p>}
      {upload.error && <p className="mt-1 text-sm text-danger">{errorMessage(upload.error)}</p>}
      <p className="mt-1 text-xs text-muted">Only upload fonts you're licensed to use on a website.</p>
    </div>
  );
}

function ColorField({ label, value, onChange, unset = "Clinic's" }: { label: string; value: string | null; onChange: (v: string | null) => void; unset?: string }) {
  return (
    <div>
      <p className="mb-1 text-sm font-semibold text-ink-soft">{label}</p>
      <div className="flex items-center gap-2">
        <input type="color" aria-label={label} value={value ?? "#1F1A1C"} onChange={(e) => onChange(e.target.value.toUpperCase())} className="h-9 w-12 cursor-pointer rounded border border-line" />
        {value ? (
          <button type="button" className="text-sm text-muted underline" onClick={() => onChange(null)}>
            {unset}
          </button>
        ) : (
          <span className="text-sm text-muted">{unset}</span>
        )}
      </div>
    </div>
  );
}

// ── Event day

export function CheckInPage() {
  const { occurrenceId = "" } = useParams();
  const qc = useQueryClient();
  const view = useQuery({ queryKey: ["check-in", occurrenceId], queryFn: () => get<CheckInView>(`/api/event-occurrences/${occurrenceId}/check-in`), refetchInterval: 30_000 });
  const eventId = view.data?.occurrence.eventId;
  const ev = useQuery({ queryKey: ["event", eventId], queryFn: () => get<EventDetail>(`/api/events/${eventId}`), enabled: !!eventId });
  const [q, setQ] = useState("");
  const openVisit = useNavigate();
  const setOpenId = (id: string) => openVisit(`/visits/${id}`);
  const [walkUp, setWalkUp] = useState(false);
  const [scanning, setScanning] = useState(false);
  const step = useMutation({
    mutationFn: (v: { id: string; to: AppointmentStatus }) => post(`/api/appointments/${v.id}/status`, { status: v.to }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["check-in", occurrenceId] }),
  });

  if (view.isLoading) return <Loading />;
  if (view.error || !view.data) return <Alert>{errorMessage(view.error)}</Alert>;
  const { occurrence: o, appointments } = view.data;
  const term = q.trim().toLowerCase();
  const shown = appointments
    .filter((a) => !term || a.bookingReference.toLowerCase() === term || displayName(a.client).toLowerCase().includes(term) || `${a.client.firstName} ${a.client.lastName}`.toLowerCase().includes(term))
    .sort((a, b) => a.start.localeCompare(b.start));
  const staffName = (id: string | null) => o.staff.find((s) => s.id === id)?.name ?? "";
  const counts = { here: appointments.filter((a) => ["arrived", "active", "completed"].includes(a.status)).length, total: appointments.filter((a) => isOpenStatus(a.status)).length };

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <BackLink to={`/events/${o.eventId}`} label={o.eventName} />
      <PageHeader
        title={`Check-in: ${o.eventName}`}
        description={`${dayLabel(o.start)}, ${time12(o.start)}–${time12(o.end)} · ${counts.here} of ${counts.total} here · ${o.chairCount} chairs`}
        action={
          <Button variant="primary" onClick={() => setWalkUp(true)} disabled={!ev.data}>
            Walk-up
          </Button>
        }
      />
      <div className="flex gap-2">
        <Field className="flex-1" label="Find by name or confirmation code" value={q} onChange={(e) => setQ(e.target.value)} />
        {"BarcodeDetector" in window && (
          <Button className="self-end" onClick={() => setScanning(true)}>
            Scan code
          </Button>
        )}
      </div>
      {step.error && <Alert>{errorMessage(step.error)}</Alert>}
      <ul className="flex flex-col gap-2">
        {shown.map((a) => (
          <CheckInRow key={a.id} a={a} staffName={staffName} busy={step.isPending && step.variables?.id === a.id} onOpen={() => setOpenId(a.id)} onStep={(to) => step.mutate({ id: a.id, to })} />
        ))}
        {!shown.length && <li className="py-4 text-sm text-muted">{term ? "No one matches." : "No one booked yet."}</li>}
      </ul>
      {walkUp && ev.data && (
        <WalkUpDialog
          occurrenceId={occurrenceId}
          event={ev.data}
          onClose={() => setWalkUp(false)}
          onDone={() => (setWalkUp(false), void qc.invalidateQueries({ queryKey: ["check-in", occurrenceId] }))}
        />
      )}
      {scanning && <ScanDialog onClose={() => setScanning(false)} onCode={(code) => (setQ(code), setScanning(false))} />}
    </div>
  );
}

function CheckInRow({ a, staffName, busy, onOpen, onStep }: { a: AppointmentView; staffName: (id: string | null) => string; busy: boolean; onOpen: () => void; onStep: (to: AppointmentStatus) => void }) {
  const next = NEXT_ACTION[a.status];
  const nav = useNavigate();
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-[var(--radius-panel)] border border-line bg-white px-4 py-3">
      <button onClick={onOpen} className="min-w-0 flex-1 text-left">
        <span className="font-semibold tabular-nums">{time12(a.start)}</span> <span className="font-semibold">{displayName(a.client)}</span>
        <span className="block text-sm text-ink-soft">
          {a.serviceName}
          {a.addOns.length > 0 && ` + ${a.addOns.map((x) => x.name).join(", ")}`} · {a.staffId ? staffName(a.staffId) : "No nurse"} · {a.bookingReference}
          {a.bookingSource === "walk_in" && " · walk-up"}
        </span>
        {a.client.schedulingAlert && <span className="block text-sm font-semibold text-wait">⚠ {a.client.schedulingAlert}</span>}
        <FormsBadge a={a} />
        {a.status !== "cancelled" && a.status !== "completed" && <GfeBadge g={a.gfe} compact />}
      </button>
      <Badge tone={STATUS_TONE[a.status]}>{STATUS_LABELS[a.status]}</Badge>
      {next && (
        <Button variant={a.status === "confirmed" ? "primary" : undefined} busy={busy} onClick={() => (next.visit ? nav(`/visits/${a.id}`) : onStep(next.to))}>
          {next.label}
        </Button>
      )}
    </li>
  );
}

function WalkUpDialog({ occurrenceId, event, onClose, onDone }: { occurrenceId: string; event: EventDetail; onClose: () => void; onDone: () => void }) {
  // What's on this event's menu, at the event's prices; add-ons under the service they ride on.
  const catalog = useCatalog();
  const onMenu = new Map(event.menu.map((m) => [m.catalogItemId, m.priceCents]));
  // Staff can give something off the menu (at its usual price), with a reason.
  const [offMenuShown, setOffMenuShown] = useState(false);
  const [offMenuReason, setOffMenuReason] = useState("");
  const items = (catalog.data?.items ?? []).filter((i) => onMenu.has(i.id) || (offMenuShown && i.status === "active" && (i.type === "service" || i.type === "add_on")));
  const services = items.filter((i) => i.type === "service");
  const [p, setP] = useState({ firstName: "", lastName: "", dob: "", mobile: "", email: "" });
  const [picked, setPicked] = useState<VisitServiceRequest[]>([]);
  const [servicesReason, setServicesReason] = useState("");
  const chosen = picked.length ? picked : services[0] ? [{ serviceId: services[0].id, addOnIds: [] }] : [];
  const add = useMutation({
    mutationFn: () => post<AppointmentView>(`/api/event-occurrences/${occurrenceId}/walk-ups`, { client: { ...p, mobile: p.mobile || null, email: p.email.trim() || null }, services: chosen, servicesReason, offMenuReason }),
    onSuccess: onDone,
  });
  return (
    <Dialog
      title="Walk-up"
      onClose={onClose}
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" busy={add.isPending} disabled={!p.firstName || !p.lastName || !p.dob || !chosen.length || (chosen.some((s) => !onMenu.has(s.serviceId) || s.addOnIds.some((x) => !onMenu.has(x))) && offMenuReason.trim().length < 3)} onClick={() => add.mutate()}>
            Check in
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" autoFocus value={p.firstName} onChange={(e) => setP({ ...p, firstName: e.target.value })} />
          <Field label="Last name" value={p.lastName} onChange={(e) => setP({ ...p, lastName: e.target.value })} />
          <DateOfBirthField value={p.dob} onChange={(d) => setP({ ...p, dob: d })} />
          <Field label="Mobile" type="tel" value={p.mobile} onChange={(e) => setP({ ...p, mobile: e.target.value })} />
          <Field label="Email" type="email" hint="Required for adults." value={p.email} onChange={(e) => setP({ ...p, email: e.target.value })} />
        </div>
        <ServicesPicker items={items} services={services} value={chosen} onChange={setPicked} price={(x) => onMenu.get(x.id) ?? x.priceCents} reason={servicesReason} onReason={setServicesReason} />
        <Checkbox label="Show services and add-ons not on this event's menu" checked={offMenuShown} onChange={setOffMenuShown} />
        {chosen.some((s) => !onMenu.has(s.serviceId) || s.addOnIds.some((x) => !onMenu.has(x))) && (
          <Field label="Why something off the menu (logged)" value={offMenuReason} onChange={(e) => setOffMenuReason(e.target.value)} hint="Priced at its usual price." />
        )}
        <p className="text-sm text-muted">They're placed in the next free chair with the next free nurse, and checked in.</p>
        {add.error && <Alert>{errorMessage(add.error)}</Alert>}
      </div>
    </Dialog>
  );
}

/** Camera scan of a confirmation QR code (browsers that support it). */
function ScanDialog({ onClose, onCode }: { onClose: () => void; onCode: (code: string) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let stream: MediaStream | null = null;
    let stop = false;
    const Detector = (window as unknown as { BarcodeDetector: new (o: { formats: string[] }) => { detect: (v: HTMLVideoElement) => Promise<{ rawValue: string }[]> } }).BarcodeDetector;
    const detector = new Detector({ formats: ["qr_code"] });
    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (!video.current) return;
        video.current.srcObject = stream;
        await video.current.play();
        while (!stop) {
          const found = await detector.detect(video.current).catch(() => []);
          if (found[0]?.rawValue) {
            onCode(found[0].rawValue.trim());
            return;
          }
          await new Promise((r) => setTimeout(r, 250));
        }
      } catch {
        setError("The camera couldn't start. Type the confirmation code instead.");
      }
    })();
    return () => {
      stop = true;
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, [onCode]);
  return (
    <Dialog title="Scan confirmation code" onClose={onClose}>
      {error ? <Alert>{error}</Alert> : <video ref={video} className="w-full rounded" muted playsInline />}
    </Dialog>
  );
}
